import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Sparkles, X, Wand2, Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { LeadsStore } from '../../lib/leadsStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Phrases that strongly suggest a second borrower exists in the note
const CO_KEYWORDS = [
  'co-borrower', 'co borrower', 'coborrower',
  'co-signer', 'co signer', 'cosigner',
  'co-applicant', 'co applicant', 'coapplicant',
  'spouse', 'husband', 'wife', 'partner',
  'fiance', 'fiancé', 'fiancée', 'fiancee',
  'her husband', 'his wife', 'their spouse',
  'jointly with', 'buying with', 'signing with', 'applying with',
];

// Look for "<keyword> <Name>" or "<keyword> named <Name>" using a *case-sensitive*
// inner pattern so we only capture proper nouns. The keyword list is matched
// case-insensitively via .toLowerCase() upstream.
const KEYWORD_PATTERN = '(?:spouse|wife|husband|partner|fianc[eé]e?|co[-\\s]?borrower|co[-\\s]?signer|co[-\\s]?applicant)';
const NAME_REGEX_GROUPS = [
  // "her husband Khalid", "his wife Emily", "spouse named Sarah Smith"
  new RegExp(`${KEYWORD_PATTERN}\\s+(?:is\\s+|named\\s+|,\\s*)?([A-Z][a-z]+(?:\\s+[A-Z][a-z]+)?)`, ''),
  // "and his/her spouse Khalid"
  new RegExp(`(?:and\\s+(?:her|his|their)\\s+${KEYWORD_PATTERN})\\s+([A-Z][a-z]+(?:\\s+[A-Z][a-z]+)?)`, ''),
];

function detectCoBorrower(text) {
  if (!text) return { hit: false };
  const lower = text.toLowerCase();
  const matched = CO_KEYWORDS.find((kw) => lower.includes(kw));
  if (!matched) return { hit: false };
  // Try to extract a probable name (case-sensitive on purpose)
  let name = null;
  for (const re of NAME_REGEX_GROUPS) {
    const m = text.match(re);
    if (m && m[1] && m[1].length > 1) { name = m[1].trim(); break; }
  }
  return { hit: true, keyword: matched, name };
}

const dismissedFromLead = (lead) => !!(lead && lead.nudges_dismissed && lead.nudges_dismissed.co_borrower);

/**
 * Proactive banner: when a lead has notes mentioning a spouse/partner/co-signer
 * but no co_borrower yet, surface a one-click "Build co-borrower from notes" CTA.
 *
 * Dismissal state lives on the lead doc (`lead.nudges_dismissed.co_borrower`) so
 * it follows the user across devices. NO localStorage usage here.
 *
 * Props:
 *   lead — the full lead object (must include id, co_borrower, nudges_dismissed)
 *   onBuilt() — optional callback after a successful build
 */
export default function CoBorrowerNudge({ lead, onBuilt }) {
  const leadId = lead?.id;
  const hasCo = !!(lead?.co_borrower && (lead.co_borrower.firstName || lead.co_borrower.lastName));
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [building, setBuilding] = useState(false);
  const dismissed = dismissedFromLead(lead);

  useEffect(() => {
    if (!leadId) return;
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/notes?leadId=${encodeURIComponent(leadId)}`)
      .then((r) => setNotes(r.data || []))
      .catch(() => setNotes([]))
      .finally(() => setLoading(false));
  }, [leadId]);

  // Refresh notes when other components add them
  useEffect(() => {
    const onNoteChange = () => {
      if (!leadId) return;
      axios.get(`${BACKEND_URL}/api/notes?leadId=${encodeURIComponent(leadId)}`)
        .then((r) => setNotes(r.data || [])).catch(() => {});
    };
    window.addEventListener('kf:notes-changed', onNoteChange);
    return () => window.removeEventListener('kf:notes-changed', onNoteChange);
  }, [leadId]);

  const detection = useMemo(() => {
    if (!notes.length) return { hit: false };
    // Concatenate all note bodies + lead.notes (the synopsis)
    const corpus = [
      ...(lead?.notes ? [lead.notes] : []),
      ...notes.map((n) => n.body || ''),
    ].join('\n');
    return detectCoBorrower(corpus);
  }, [notes, lead?.notes]);

  if (!leadId || hasCo || dismissed || loading || !detection.hit) return null;

  const dismiss = () => {
    if (!leadId) return;
    LeadsStore.update(leadId, {
      nudges_dismissed: { ...(lead.nudges_dismissed || {}), co_borrower: true },
    }).catch(() => {});
  };

  const build = async () => {
    setBuilding(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/wingman/build_application`, {
        lead_id: leadId,
      });
      const coBorrower = r.data.co_borrower || {};
      const leadPatch = r.data.lead_patch || {};
      const fields = r.data.fields || {};

      // Fire app-fill events for primary fields too (so if the LO is on the Profile tab
      // they get the synthesis as well — same behavior as the modal)
      for (const [field, value] of Object.entries(fields)) {
        window.dispatchEvent(new CustomEvent('wingman:fillApp', { detail: { field, value } }));
      }

      if (Object.keys(leadPatch).length) {
        await LeadsStore.update(leadId, leadPatch);
      }

      const coCount = Object.keys(coBorrower).length;
      if (coCount === 0) {
        toast('No co-borrower fields could be extracted', { description: 'The note mentioned a partner but no income/employer/credit details to pull yet.' });
      } else {
        const coName = [coBorrower.firstName, coBorrower.lastName].filter(Boolean).join(' ') || 'the co-borrower';
        toast.success(`Built ${coName}'s profile`, {
          description: `${coCount} co-borrower field${coCount === 1 ? '' : 's'} populated. ${r.data.summary || ''}`,
          duration: 9000,
        });
      }
      // Auto-dismiss after a successful build (server-persisted)
      LeadsStore.update(leadId, {
        nudges_dismissed: { ...(lead.nudges_dismissed || {}), co_borrower: true },
      }).catch(() => {});
      onBuilt?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Wingman could not build the co-borrower profile.');
    } finally {
      setBuilding(false);
    }
  };

  const personLabel = detection.name ? <><span className="font-bold text-neutral-900">{detection.name}</span></> : 'a co-borrower';
  const leadFirstName = (lead?.name || '').split(' ')[0] || 'this lead';

  return (
    <div
      data-testid="coborrower-nudge"
      className="relative rounded-2xl border bg-gradient-to-r from-[hsl(var(--kf-gold-soft))] via-white to-[hsl(var(--kf-gold-soft))] border-[hsl(var(--kf-gold))]/40 px-5 py-4 flex flex-wrap items-center gap-4 rise-in overflow-hidden"
    >
      {/* Decorative glow */}
      <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-[hsl(var(--kf-gold))]/20 blur-2xl pointer-events-none" />

      <div className="flex items-center gap-3 flex-1 min-w-0 relative">
        <div className="h-10 w-10 rounded-xl bg-neutral-900 text-[hsl(var(--kf-gold))] flex items-center justify-center shrink-0">
          <Users className="h-5 w-5" strokeWidth={2.5} />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <Sparkles className="h-3 w-3 text-[hsl(var(--kf-gold-deep))]" />
            <span className="text-[10.5px] uppercase tracking-[0.22em] text-[hsl(var(--kf-gold-deep))] font-bold">Wingman noticed</span>
          </div>
          <div className="text-[13px] text-neutral-800 mt-0.5 leading-snug">
            Your notes mention {personLabel} alongside {leadFirstName}. Want me to build the co-borrower profile from the notes?
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 relative">
        <button
          data-testid="coborrower-nudge-dismiss"
          onClick={dismiss}
          disabled={building}
          className="h-9 px-3 rounded-md border hairline bg-white text-[12px] font-semibold text-neutral-600 hover:border-neutral-400 disabled:opacity-50"
        >
          Not now
        </button>
        <button
          data-testid="coborrower-nudge-build"
          onClick={build}
          disabled={building}
          className="h-9 px-4 rounded-md btn-gold text-[12.5px] font-bold flex items-center gap-2 disabled:opacity-50"
        >
          {building ? (
            <><Loader2 className="h-4 w-4 animate-spin" /> Reading notes…</>
          ) : (
            <><Wand2 className="h-4 w-4" strokeWidth={2.5} /> Build co-borrower</>
          )}
        </button>
        <button
          aria-label="Close"
          onClick={dismiss}
          disabled={building}
          className="h-8 w-8 rounded-md hover:bg-neutral-100 flex items-center justify-center text-neutral-400 hover:text-neutral-700 disabled:opacity-50"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
